import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import { supabase } from '../supabase/supabase';
import { logger } from '../utils/logger';

const { FiPlus, FiChevronDown, FiCpu, FiEdit3, FiGrid, FiUsers, FiPlayCircle, FiLock, FiCheckCircle } = FiIcons;

export default function Courses() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [courses, setCourses] = useState([]);
  const [userEnrollments, setUserEnrollments] = useState({});
  const [coursePrereqs, setCoursePrereqs] = useState({});

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const [coursesRes, enrollRes, prereqRes] = await Promise.all([
        supabase.from('courses_2024').select('*').order('created_at', { ascending: false }),
        supabase.from('enrollments_2024').select('course_id, status, progress_percent'),
        supabase.from('course_prerequisites_2024').select('*')
      ]);

      if (coursesRes.error) throw coursesRes.error;

      const enrollMap = {};
      enrollRes.data?.forEach(e => enrollMap[e.course_id] = e);
      setUserEnrollments(enrollMap);

      const preMap = {};
      prereqRes.data?.forEach(p => {
        if (!preMap[p.course_id]) preMap[p.course_id] = [];
        preMap[p.course_id].push(p.prerequisite_id);
      });
      setCoursePrereqs(preMap);

      setCourses(coursesRes.data || []);
    } catch (error) {
      logger.error('Failed to fetch academy data', { error: error.message });
      addToast(error.message, 'error');
    } finally {
      setFetching(false);
    }
  };

  const isCourseLocked = (courseId) => {
    const prereqs = coursePrereqs[courseId] || [];
    if (prereqs.length === 0) return false;
    return prereqs.some(preId => {
      const enrollment = userEnrollments[preId];
      return !enrollment || enrollment.status !== 'completed';
    });
  };

  const handleManualCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    const title = formData.get('title');
    const description = formData.get('description');
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Authentication required");

      const { data, error } = await supabase
        .from('courses_2024')
        .insert([{ 
          title, 
          description, 
          image_url: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=250&fit=crop',
          status: 'Draft',
          coach_id: user.id
        }])
        .select();
      
      if (error) throw error;
      setCourses([data[0], ...courses]);
      setIsModalOpen(false);
      addToast("Academy module created");
      navigate(`/courses/edit/${data[0].id}`);
    } catch (error) {
      logger.error('Course Creation Failure', { title, error: error.message });
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <div className="h-[60vh] flex flex-col items-center justify-center space-y-6">
      <div className="w-16 h-16 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
      <p className="text-slate-500 font-black text-[10px] uppercase tracking-[0.3em] animate-pulse">Loading Academy Grid...</p>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white uppercase tracking-tight">SessionHQ Academy</h1>
          <p className="text-slate-400 mt-1">Manage your intellectual property and curriculum.</p>
        </div>
        <div className="relative">
          <button onClick={() => setShowCreateDropdown(!showCreateDropdown)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-indigo-600/20">
            <SafeIcon icon={FiPlus} /> Create New <SafeIcon icon={FiChevronDown} className={`transition-transform ${showCreateDropdown ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {showCreateDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowCreateDropdown(false)} />
                <motion.div initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute right-0 mt-2 w-72 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl z-20 overflow-hidden">
                  <button onClick={() => { setShowCreateDropdown(false); navigate('/ai-architect'); }} className="w-full p-4 flex items-start gap-4 hover:bg-slate-800 transition-colors text-left group border-b border-slate-800">
                    <div className="w-10 h-10 rounded-lg bg-indigo-600/10 text-indigo-400 flex items-center justify-center"><SafeIcon icon={FiCpu} /></div>
                    <div><p className="text-sm font-bold text-white">Generate with AI</p><p className="text-[10px] text-slate-500 uppercase">Outlines in seconds</p></div>
                  </button>
                  <button onClick={() => { setShowCreateDropdown(false); setIsModalOpen(true); }} className="w-full p-4 flex items-start gap-4 hover:bg-slate-800 transition-colors text-left group">
                    <div className="w-10 h-10 rounded-lg bg-slate-800 group-hover:bg-indigo-600 text-slate-400 group-hover:text-white flex items-center justify-center transition-colors"><SafeIcon icon={FiEdit3} /></div>
                    <div><p className="text-sm font-bold text-white">Create Manually</p><p className="text-[10px] text-slate-500 uppercase">Build from scratch</p></div>
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {courses.map((course) => {
          const locked = isCourseLocked(course.id);
          const enrollment = userEnrollments[course.id];
          const isCompleted = enrollment?.status === 'completed';

          return (
            <motion.div layout key={course.id} className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden group shadow-xl relative">
              <div className="relative h-48 overflow-hidden">
                <img src={course.image_url} alt={course.title} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${locked ? 'grayscale blur-[2px] opacity-50' : ''}`} />
                <div className="absolute top-4 right-4 flex gap-2">
                  <div className="px-3 py-1 bg-slate-900/80 backdrop-blur-md rounded-full border border-slate-700 text-[10px] font-bold text-white uppercase tracking-widest">{course.status}</div>
                  {isCompleted && <div className="p-1.5 bg-emerald-500 text-white rounded-full shadow-lg"><SafeIcon icon={FiCheckCircle} /></div>}
                </div>
                
                {locked && (
                  <div className="absolute inset-0 bg-slate-950/60 flex flex-col items-center justify-center text-white backdrop-blur-[1px]">
                    <SafeIcon icon={FiLock} className="text-3xl mb-2" />
                    <span className="text-[10px] font-bold uppercase tracking-widest">Prerequisites Required</span>
                  </div>
                )}

                <div className={`absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6 ${locked ? 'hidden' : ''}`}>
                  <button onClick={() => navigate(`/courses/edit/${course.id}`)} className="w-full bg-white text-slate-900 py-2 rounded-xl font-bold flex items-center justify-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform">
                    <SafeIcon icon={FiGrid} /> Edit Curriculum
                  </button>
                </div>
              </div>

              <div className="p-6">
                <h3 className={`text-lg font-bold mb-4 line-clamp-1 transition-colors uppercase tracking-tight ${locked ? 'text-slate-500' : 'text-white group-hover:text-indigo-400'}`}>{course.title}</h3>
                <div className="flex justify-between items-center text-sm text-slate-400 mb-6 font-medium">
                  <button onClick={() => navigate(`/courses/enrollments/${course.id}`)} className="flex items-center gap-2 hover:text-white transition-colors" >
                    <SafeIcon icon={FiUsers} className="text-indigo-400" /> {course.students_count} Students
                  </button>
                  <div className="font-bold text-white">${course.revenue}</div>
                </div>

                <div className="flex gap-2">
                  <button 
                    onClick={() => navigate(`/courses/player/${course.id}`)} 
                    disabled={locked}
                    className={`flex-1 py-2.5 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${locked ? 'bg-slate-800 text-slate-600 cursor-not-allowed' : 'bg-indigo-600/10 hover:bg-indigo-600 text-indigo-400 hover:text-white'}`}
                  >
                    <SafeIcon icon={locked ? FiLock : FiPlayCircle} /> {locked ? 'Locked' : 'Access'}
                  </button>
                  <button onClick={() => navigate(`/courses/enrollments/${course.id}`)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-xs">
                    <SafeIcon icon={FiUsers} /> Manage
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <FormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Create New Academy Module" onSubmit={handleManualCreate} loading={loading}>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-400">Academy Title</label>
            <input name="title" required className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="e.g. High-Ticket Sales Mastery" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-400">Description</label>
            <textarea name="description" className="w-full bg-slate-800 border-none rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-indigo-500 outline-none resize-none h-24" placeholder="Briefly describe the learning outcome..." />
          </div>
        </div>
      </FormModal>
    </div>
  );
}